import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { SearchPill } from "@/components/SearchProgress";
import { CalendarCheck, UserCircle, Users, Brain, BarChart3, CreditCard, LogOut, RotateCcw, Loader2, Radar, Shield, UsersRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import OfferBanner from "@/components/OfferBanner";
import { LogoImage } from "@/components/Brand";
import TypewriterSearch from "@/components/effects/TypewriterSearch";
import Onboarding from "@/components/Onboarding";
import { initials } from "@/lib/format";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const DEMO_EMAIL = "demo@captaccion.app";

const NAV_GROUPS = [
  { heading: "Hoy", items: [
    { to: "/app/hoy", label: "Qué hacer hoy", icon: CalendarCheck },
    { to: "/app/oportunidades", label: "Oportunidades", icon: Radar },
  ] },
  { heading: "Relaciones", items: [
    { to: "/app/crm", label: "CRM y contactos", icon: Users },
    { to: "/app/canales", label: "Mi perfil en redes", icon: UserCircle },
  ] },
  { heading: "Tu negocio", items: [
    { to: "/app/brain", label: "Mi negocio", icon: Brain },
    { to: "/app/metricas", label: "Métricas", icon: BarChart3 },
    { to: "/app/cuenta", label: "Cuenta y plan", icon: CreditCard },
  ] },
];
const NAV = NAV_GROUPS.flatMap((g) => g.items);

const ADMIN_ITEM = { to: "/app/admin", label: "Usuarios (admin)", icon: Shield };
const TEAM_ITEM = { to: "/app/equipo", label: "Mi equipo", icon: UsersRound };   // v13: solo la cuenta madre (Business)

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [resetting, setResetting] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [ready, setReady] = useState(false);
  const [q, setQ] = useState("");
  // «Restaurar demo» borra los contactos: solo tiene sentido (y solo se muestra) en la cuenta de demostración.
  const isDemo = (user?.email || "").toLowerCase() === DEMO_EMAIL;

  // P3-D: estado de impersonación (ver como usuario · solo lectura).
  const impersonation = (() => { try { return JSON.parse(localStorage.getItem("ca_impersonation") || "null"); } catch { return null; } })();
  const exitImpersonation = () => {
    const adminTok = localStorage.getItem("ca_admin_token");
    if (adminTok) localStorage.setItem("ca_token", adminTok);
    localStorage.removeItem("ca_admin_token");
    localStorage.removeItem("ca_impersonation");
    window.location.href = "/app/admin";
  };

  useEffect(() => {
    if (!user || !user.id) return;
    if (impersonation) { setOnboarding(false); setReady(true); return; }
    const checkOnboarding = () => {
      // Quien depende de una cuenta madre usa el negocio de esa cuenta: no hace el cuestionario inicial.
      if (user.team_role === "member") { setOnboarding(false); setReady(true); return; }
      setReady(false);
      api.get("/business-brain").then((r) => {
        if (!r.data || !r.data.onboarding_completed) { setOnboarding(true); return; }
        setOnboarding(false);
      }).catch(() => { setOnboarding(false); }).finally(() => setReady(true));
    };
    // v13: invitación a un equipo pendiente (se guardó al abrir /unirse sin sesión).
    let invite = null;
    try { invite = localStorage.getItem("ca_team_invite"); } catch { invite = null; }
    if (invite) {
      try { localStorage.removeItem("ca_team_invite"); } catch { /* nada */ }
      setReady(false);
      api.post("/team/accept", { token: invite })
        .then((r) => { toast.success(`Ya formas parte del equipo de ${r.data.owner?.name || r.data.owner?.email}.`); window.location.reload(); })
        .catch((e) => { toast.error(apiError(e)); checkOnboarding(); });
      return;
    }
    checkOnboarding();
  }, [user]);

  const finishOnboarding = () => {
    setOnboarding(false);
    if (user && user.id) localStorage.setItem(`ca_welcomed_${user.id}`, "1");
    window.dispatchEvent(new Event("ca:data-changed"));
    navigate("/app/brain");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/acceso");
  };

  const restoreDemo = async () => {
    setResetting(true);
    try {
      const res = await api.post("/demo/reset");
      toast.success(`Datos de demostración restaurados (${res.data.count} contactos).`);
      window.dispatchEvent(new Event("ca:data-changed"));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setResetting(false);
    }
  };

  if (user && user.id && !ready) {
    return (
      <div className="ca-app flex items-center justify-center">
        <Loader2 className="animate-spin" size={26} style={{ color: "var(--ca-gold)" }} />
      </div>
    );
  }

  if (onboarding) return <Onboarding onDone={finishOnboarding} />;

  return (
    <div className="ca-app">
      {impersonation && (
        <div data-testid="impersonation-banner" className="fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-3 px-4 py-2 text-xs font-bold text-black" style={{ background: "var(--ca-gold)" }}>
          <Shield size={14} /> Viendo como {impersonation.email} · Modo solo lectura
          <button onClick={exitImpersonation} data-testid="impersonation-exit" className="rounded bg-black/25 px-2 py-0.5 text-white">Salir</button>
        </div>
      )}
      {/* Sidebar — desktop */}
      <aside className="ca-sidebar fixed inset-y-0 left-0 z-30 hidden w-64 flex-col lg:flex">
        <div className="flex h-16 items-center border-b border-[var(--ca-line)] px-6">
          <LogoImage size="md" />
        </div>
        <nav className="flex-1 space-y-6 px-3 py-6">
          {NAV_GROUPS.map((group) => (
            <div key={group.heading} className="space-y-1.5">
              <p className="ca-nav-group">{group.heading}</p>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.to.split("/").pop()}`}
                  className={({ isActive }) => `ca-navlink ${isActive ? "is-active" : ""}`}
                >
                  <item.icon size={18} strokeWidth={2} />
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
          {user?.team_role === "owner" && (
            <div className="space-y-1.5">
              <p className="ca-nav-group">Business</p>
              <NavLink to={TEAM_ITEM.to} data-testid="nav-team" className={({ isActive }) => `ca-navlink ${isActive ? "is-active" : ""}`}>
                <TEAM_ITEM.icon size={18} strokeWidth={2} />
                {TEAM_ITEM.label}
              </NavLink>
            </div>
          )}
          {user?.is_admin && (
            <div className="space-y-1.5">
              <p className="ca-nav-group">Administración</p>
              <NavLink
                to={ADMIN_ITEM.to}
                data-testid="nav-admin"
                className={({ isActive }) => `ca-navlink ${isActive ? "is-active" : ""}`}
              >
                <ADMIN_ITEM.icon size={18} strokeWidth={2} />
                {ADMIN_ITEM.label}
              </NavLink>
            </div>
          )}
        </nav>
        <div className="border-t border-[var(--ca-line)] p-3">
          {false && isDemo && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="sidebar-reset-demo" className="ca-btn-ghost ca-btn-sm ca-btn-block mb-2">
                <RotateCcw size={14} /> Restaurar demo
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Restaurar los datos de demostración?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se borrarán tus contactos actuales y volverán los 10 de ejemplo. Útil antes de enseñarlo a alguien.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={restoreDemo} disabled={resetting} data-testid="confirm-reset-demo">
                  {resetting ? "Restaurando…" : "Sí, restaurar"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          )}
          <div className="mt-1 flex items-center gap-3 rounded-xl border border-[var(--ca-line)] px-3 py-2.5" style={{ background: "var(--ca-surface)" }}>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg text-xs font-bold" style={{ background: "var(--ca-metal)", color: "var(--ca-gold-ink)" }}>
              {initials(user?.name || user?.email || "")}
            </span>
            <NavLink to="/app/cuenta" data-testid="sidebar-account" title={user?.email || ""} className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-[var(--ca-text)]">{user?.name || "Tu cuenta"}</p>
              <p className="truncate text-[11px] text-[var(--ca-subtle)]">{isDemo ? "Cuenta de demostración" : `Plan ${user?.plan_label || "activo"}`}</p>
            </NavLink>
            <button onClick={handleLogout} data-testid="logout-button" title="Salir" className="text-[var(--ca-subtle)] transition-colors duration-150 hover:text-[var(--ca-gold-hi)]">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        {/* Mobile top bar */}
        <div className="ca-mobilebar sticky top-0 z-20 flex h-14 items-center justify-between px-4 lg:hidden">
          <LogoImage size="sm" />
          <button onClick={handleLogout} className="text-[var(--ca-muted)]" title="Salir">
            <LogOut size={18} />
          </button>
        </div>

        <div className="ca-topbar sticky top-0 z-20 hidden h-16 items-center gap-4 px-6 lg:flex" data-testid="dash-topbar">
          <TypewriterSearch value={q} onChange={setQ} />
          {!isDemo && <SearchPill />}
        </div>

        <main className="mx-auto max-w-6xl px-4 pb-28 pt-6 sm:px-6 lg:px-10 lg:pb-16 lg:pt-10">
          {isDemo && (
            <div data-testid="demo-banner" className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl px-4 py-3 text-sm"
              style={{ border: "1px solid rgba(224,185,112,.35)", background: "linear-gradient(90deg,rgba(224,185,112,.12),rgba(224,185,112,.03))" }}>
              <span className="font-mono text-[11px] tracking-[.14em] text-[var(--ca-gold)]">DEMO</span>
              <span className="text-[var(--ca-muted)]">Estás viendo un negocio de ejemplo con datos inventados. Puedes recorrerlo todo; los cambios y las búsquedas están desactivados.</span>
              <button type="button" onClick={async () => { await logout(); navigate("/registro"); }} className="ca-btn-gold ca-btn-sm" style={{ marginLeft: "auto" }} data-testid="demo-banner-signup">Crear mi cuenta</button>
            </div>
          )}
          {!isDemo && <div className="mb-6 empty:hidden"><OfferBanner /></div>}
          <Outlet />
        </main>
      </div>

      {/* Bottom nav — mobile */}
      <nav className="ca-bottomnav fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 lg:hidden">
        {NAV.slice(0, 5).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`bottomnav-${item.to.split("/").pop()}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold transition-colors duration-150 ${
                isActive ? "text-[var(--ca-gold-hi)]" : "text-[var(--ca-muted)]"
              }`
            }
          >
            <item.icon size={19} strokeWidth={2} />
            {item.label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>

    </div>
  );
}
